<template>
  <section class="py-16 md:container overflow-hidden" aria-labelledby="compare-table-heading">
    <div class="flex flex-row gap-2 items-center justify-between p-4 border-b">
      <h2 id="compare-table-heading" class="fluid-xl leading-tight font-medium w-full md:w-auto break-words">
        {{ heading }}
      </h2>
      <ArrowBtnNav
        v-if="isScrollable"
        class="block"
        :left-arrow-disabled="isFirstSlideVisible"
        :right-arrow-disabled="isLastSlideVisible"
        :aria-label="$t('compare.navigation')"
        @onLeftClick="scrollSlidesLeft"
        @onRightClick="scrollSlidesRight"
      />
    </div>

    <!-- Compare Section -->
    <div
      ref="container"
      class="root-grid grid w-full"
      :style="cssVars"
      role="table"
      :aria-label="$t('compare.tableLabel')"
      aria-describedby="compare-table-heading"
    >
      <!-- Sticky Area: Labels + First Product -->
      <div class="sticky-area sticky left-0 z-10 grid my-12 gap-y-4 shadow-xl">
        <!-- Labels Column -->
        <div class="labels-column bg-putty text-copy px-1 sm:px-3 py-6 grid grid-rows-subgrid" role="rowgroup">
          <div role="row">
            <div role="columnheader" class="sr-only">{{ $t('compare.attributeColumn') }}</div>
          </div>
          <FixedAspect :ratio="0.55" role="row" aria-hidden="true">
            <div role="columnheader" class="sr-only">{{ $t('compare.imageRow') }}</div>
          </FixedAspect>
          <div
            :class="[
              'font-medium break-words whitespace-normal text-xs md:text-sm hyphens-auto border-b border-gray-lighter pb-2',
              !showVariants ? 'last-of-type:border-b-0' : '',
            ]"
            v-for="(attribute, attrIndex) in compareAttributes"
            :key="attribute.key"
            role="row"
          >
            <div role="rowheader" :id="`attribute-label-${attrIndex}`">
              {{ attribute.title }}
            </div>
          </div>
          <div
            v-if="showVariants"
            class="font-medium break-words whitespace-normal text-xs md:text-sm hyphens-auto pb-2"
            role="row"
          >
            <div role="rowheader" :id="`attribute-label-variants`">
              {{ $t('compare.variants') }}
            </div>
          </div>
        </div>

        <!-- First Product -->
        <ProductCompareCardLarge
          v-if="productList.length > 0 && makeFirstProductSticky"
          :key="productList[0].id"
          :product="productList[0]"
          :compare-attributes="compareAttributes"
          :showProductLink="showProductLink"
          :showAddToCart="showAddToCart"
          :show-variants="showVariants"
          :show-product-price="showProductPrice"
          :index="0"
          :attribute-label-ids="attributeLabelIds"
          class="first-product grid grid-rows-subgrid shadow-none"
          data-test-id="first-item"
          role="rowgroup"
        />
      </div>

      <!-- Scrollable Area: Remaining Products -->
      <div
        ref="scrollableArea"
        class="scrollable-area grid py-12 snap-mandatory snap-x border-transparent focus-visible:focus-ring-inside overflow-y-hidden"
        :class="{ 'overflow-x-auto md:overflow-x-hidden': isScrollable, 'pl-2': makeFirstProductSticky }"
        tabindex="0"
        :aria-label="$t('compare.scrollableProducts')"
        @keydown="handleKeyDown"
      >
        <div class="products-grid grid grid-flow-col">
          <ProductCompareCardLarge
            v-for="(product, idx) in makeFirstProductSticky ? productList.slice(1) : productList"
            :key="product.id"
            :product="product"
            :compare-attributes="compareAttributes"
            :showProductLink="showProductLink"
            :showAddToCart="showAddToCart"
            :show-variants="showVariants"
            :show-product-price="showProductPrice"
            :index="makeFirstProductSticky ? idx + 1 : idx"
            :attribute-label-ids="attributeLabelIds"
            :class="[
              'product-card grid grid-rows-subgrid shadow-lg snap-start scroll-ml-4',
              makeFirstProductSticky || idx > 0 ? 'mx-2' : 'mr-2',
            ]"
            data-test-id="item"
            role="rowgroup"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup async>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import FixedAspect from '@/components/common/FixedAspect';
import ProductCompareCardLarge from '@/components/shop/products/ProductCompareCardLarge';
import ArrowBtnNav from '@/components/common/ArrowBtnNav';
import { useShopifyCompareData } from '@/composables/useShopifyCompareData';
import { useHybridCompareData } from '@/composables/useHybridCompareData';

const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  showFilters: {
    type: Boolean,
    default: false,
  },
  collectionHandle: {
    type: String,
    default: null,
  },
  productComparison: {
    type: Array,
    default: () => [],
  },
  showVariants: {
    type: Boolean,
    default: false,
  },
  makeFirstProductSticky: {
    type: Boolean,
    default: false,
  },
  showProductLink: {
    type: Boolean,
    default: false,
  },
  showAddToCart: {
    type: Boolean,
    default: false,
  },
  showProductPrice: {
    type: Boolean,
    default: false,
  },
});

// Get data from composable
const { compareAttributes, productList } =
  props.productComparison.length > 0
    ? await useHybridCompareData(props.productComparison[0])
    : await useShopifyCompareData(props.collectionHandle);

// UI state
const containerWidth = ref(1024);
const visibleColumns = ref(2);
const windowWidth = ref(0);
const isFirstSlideVisible = ref(true);
const isLastSlideVisible = ref(false);
const isScrollable = ref(false);

const container = ref(null);
const scrollableArea = ref(null);
let resizeObserver = null;

const labelWidth = computed(() => {
  if (typeof window === 'undefined') return 180;
  return windowWidth.value < 768 ? 90 : 180;
});

const cardWidth = computed(() => {
  // Calculate what's left for cards after fixed costs
  const columnGap = 16;
  const remainingWidth = containerWidth.value - labelWidth.value - columnGap;

  // All cards (sticky first card + scrollable cards) share the remaining space equally
  const totalCards = 1 + visibleColumns.value; // 1 first card + visible scrollable cards
  const calculatedWidth = Math.ceil(remainingWidth / totalCards);

  return calculatedWidth;
});

const stickyWidth = computed(() => {
  return props.makeFirstProductSticky ? labelWidth.value + cardWidth.value : labelWidth.value;
});

const cssVars = computed(() => {
  let additionalRows = 1; // for header
  additionalRows += props.showVariants ? 1 : 0;
  additionalRows += props.showProductLink || props.showAddToCart || props.showProductPrice ? 1 : 0;

  return {
    '--label-width': `${labelWidth.value}px`,
    '--card-width': `${cardWidth.value}px`,
    '--sticky-width': `${stickyWidth.value}px`,
    '--row-count': (compareAttributes.value?.length || 10) + additionalRows,
  };
});

const attributeLabelIds = computed(() => {
  const ids = compareAttributes.value?.map((attr, idx) => `attribute-label-${idx}`) || [];
  if (props.showVariants) {
    ids.push('attribute-label-variants');
  }
  return ids;
});

const checkScrollability = () => {
  if (!scrollableArea.value) return;

  // Calculate if content needs scrolling
  const products = props.makeFirstProductSticky ? productList.value.length - 1 : productList.value.length;
  const gap = 16;
  const totalContentWidth = products * cardWidth.value + (products - 1) * gap;
  const availableWidth = scrollableArea.value.clientWidth - 32; // subtract px-4 padding (16px each side)
  isScrollable.value = totalContentWidth > availableWidth;

  // Update arrow button states based on scroll position
  if (isScrollable.value) {
    const { scrollLeft, scrollWidth, clientWidth } = scrollableArea.value;
    isFirstSlideVisible.value = scrollLeft <= 1;
    isLastSlideVisible.value = scrollLeft >= scrollWidth - clientWidth - 1;
  }
};

const updateDimensions = () => {
  containerWidth.value = container.value?.clientWidth || 1024;

  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth;
    visibleColumns.value = windowWidth.value < 1024 ? 1 : 2;
  }

  nextTick(() => {
    checkScrollability();
  });
};

const scrollSlidesLeft = () => {
  if (!scrollableArea.value) return;

  const scrollDistance = cardWidth.value + 16; // card width + gap
  scrollableArea.value.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
};

const scrollSlidesRight = () => {
  if (!scrollableArea.value) return;

  const scrollDistance = cardWidth.value + 16; // card width + gap
  scrollableArea.value.scrollBy({ left: scrollDistance, behavior: 'smooth' });
};

const handleKeyDown = (event) => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    scrollSlidesLeft();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    scrollSlidesRight();
  }
};

const onScrollableAreaScroll = (event) => {
  const { scrollLeft, scrollWidth, clientWidth } = event.target;

  isFirstSlideVisible.value = scrollLeft <= 1;
  isLastSlideVisible.value = scrollLeft >= scrollWidth - clientWidth - 1;
};

onMounted(async () => {
  nextTick(() => {
    updateDimensions();
  });

  if (container.value) {
    resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container.value);
  } else {
    window.addEventListener('resize', updateDimensions);
  }

  // Add scroll event listener
  if (scrollableArea.value) {
    scrollableArea.value.addEventListener('scroll', onScrollableAreaScroll);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  window.removeEventListener('resize', updateDimensions);

  // Remove scroll event listener
  if (scrollableArea.value) {
    scrollableArea.value.removeEventListener('scroll', onScrollableAreaScroll);
  }
});
</script>

<style scoped>
@property --label-width {
  syntax: '<length>';
  initial-value: 180px;
  inherits: true;
}

@property --card-width {
  syntax: '<length>';
  initial-value: 260px;
  inherits: true;
}

@property --sticky-width {
  syntax: '<length>';
  initial-value: 440px;
  inherits: true;
}

@property --first-row-height {
  syntax: '<length>';
  initial-value: 80px;
  inherits: true;
}
@property --default-row-count {
  syntax: '<integer>';
  initial-value: 10;
  inherits: true;
}

.root-grid {
  grid-template-columns: var(--sticky-width) 1fr;
  grid-template-rows: var(--first-row-height) repeat(var(--row-count, var(--default-row-count)), minmax(60px, auto));
}

.sticky-area {
  grid-template-columns: var(--label-width);
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
  column-gap: 0;
  row-gap: 16px;
}

.sticky-area:has(.first-product) {
  grid-template-columns: var(--label-width) var(--card-width);
}

.labels-column {
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
}

.first-product {
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
  width: var(--card-width);
  max-width: var(--card-width);
  min-width: var(--card-width);
  overflow: hidden;
}

.scrollable-area {
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
}

.products-grid {
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
  grid-auto-columns: var(--card-width);
  row-gap: 16px;
}

.product-card {
  grid-template-rows: subgrid;
  grid-row: 1 / -1;
}
</style>
